/**
 * AES-256-GCM decryption utility for the frontend (Web Crypto API).
 *
 * Decrypts encrypted payloads (e.g. feconfigs API responses, sensitive data)
 * using the Web Crypto API (built into all modern browsers, no dependencies).
 *
 * Types, validators, and constants are imported from the shared
 * encryption library (@content-platform/shared-utils).
 *
 * Usage:
 *   import { decryptPayload, isEncryptedPayload } from '@content-platform/web-angular-shared-data-access-okta-auth2';
 *
 *   if (isEncryptedPayload(response)) {
 *     const data = await decryptPayload(response);
 *   }
 */
import { getConfigEncryptionKey } from './secrets-endpoint.util';
import { isEncryptedPayload, hexToBytes, AES_GCM_ALGORITHM_WEB, AES_GCM_TAG_LENGTH, } from '@content-platform/shared-utils';
export { isEncryptedPayload };
/**
 * Decrypts an AES-256-GCM encrypted payload using the Web Crypto API.
 *
 * @param payload - The encrypted payload ({ iv, tag, data } — all hex)
 * @param hexKey  - Optional override for the encryption key (hex string).
 *                  Defaults to the built-in CONFIG_ENCRYPTION_KEY.
 * @returns The decrypted plaintext, parsed as a typed object
 */
export async function decryptPayload(payload, hexKey) {
    const keyHex = hexKey || getConfigEncryptionKey();
    if (!keyHex || keyHex === 'TO_BE_CONFIGURED') {
        throw new Error('CONFIG_ENCRYPTION_KEY is not configured. Cannot decrypt payload.');
    }
    const keyBytes = hexToBytes(keyHex);
    const iv = hexToBytes(payload.iv);
    const tag = hexToBytes(payload.tag);
    const ciphertext = hexToBytes(payload.data);
    const cryptoKey = await crypto.subtle.importKey('raw', keyBytes.buffer, { name: AES_GCM_ALGORITHM_WEB }, false, ['decrypt']);
    // AES-GCM in Web Crypto expects the auth tag appended to the ciphertext
    const ciphertextWithTag = new Uint8Array(ciphertext.length + AES_GCM_TAG_LENGTH);
    ciphertextWithTag.set(ciphertext);
    ciphertextWithTag.set(tag, ciphertext.byteLength);
    const decryptedBuffer = await crypto.subtle.decrypt({ name: AES_GCM_ALGORITHM_WEB, iv: iv.buffer }, cryptoKey, ciphertextWithTag.buffer);
    const decoder = new TextDecoder();
    const jsonString = decoder.decode(decryptedBuffer);
    return JSON.parse(jsonString);
}
//# sourceMappingURL=aes-decryption.util.js.map